import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../components/StructuredData';
import { PortfolioShell, SectionDivider } from './components/PortfolioShell';
import { PortfolioHero } from './components/PortfolioHero';
import { BentoFeatures } from './components/BentoFeatures';
import { ShowcaseRenderer, StandaloneBlock } from './components/ShowcaseRenderer';
import { getProject } from './registry';

export function ProjectShowcasePage() {
  const { slug } = useParams<{ slug: string }>();
  const project = slug ? getProject(slug) : undefined;
  // Hook must run unconditionally; namespace falls back when project missing.
  const { t } = useTranslation(project?.ns ?? 'portfolio');
  // card keys live in the shared 'common' ns, not the project ns.
  const { t: tCommon } = useTranslation('common');

  if (!project) return <Navigate to="/portfolio" replace />;

  const ctx = { t, prefix: project.imagePrefix };

  // Dynamic meta tags for each project
  const projectTitle = tCommon(project.cardTitleKey);
  const projectDesc = tCommon(project.cardDescKey);
  const projectImage = `https://www.happytech.dev${project.thumbnail}`;
  const projectUrl = `https://www.happytech.dev/portfolio/${slug}`;

  return (
    <PortfolioShell orbs={project.orbs}>
      <SEO
        title={projectTitle}
        description={projectDesc}
        image={projectImage}
        url={projectUrl}
        type="article"
      />
      
      <ArticleSchema
        headline={projectTitle}
        description={projectDesc}
        image={projectImage}
        author="HappyTech"
        publisher="HappyTech"
        url={projectUrl}
      />
      
      <BreadcrumbSchema
        items={[
          { name: 'หน้าหลัก', url: 'https://www.happytech.dev' },
          { name: 'ผลงาน', url: 'https://www.happytech.dev/portfolio' },
          { name: projectTitle, url: projectUrl },
        ]}
      />
      
      <PortfolioHero hero={project.hero} t={t} />

      {/* Core Features */}
      <div className="mb-32">
        <SectionDivider title={t('coreFeatures')} />
        <BentoFeatures bento={project.bento} t={t} />
      </div>

      {/* Optional blocks before the showcase heading (e.g. hero banner) */}
      {project.beforeHeading?.map((b, i) => (
        <StandaloneBlock key={i} block={b} ctx={ctx} />
      ))}

      {/* Showcase */}
      <div>
        <SectionDivider title={t(project.showcaseHeadingKey)} large />
        <ShowcaseRenderer blocks={project.showcase} ctx={ctx} />
      </div>
    </PortfolioShell>
  );
}
